/**
 * Blog layout when `document.body` has `page` + a blog-related suffix from CMS `pageType`
 * (suffix from CMS `pageType`, see king-living `contentful/utils/page-type-body-slug.js`): `page--blog-page`, `page--blog-article-img`,
 * `page--blog-article-content`.
 */

const unset = {base: 'unset', lg: 'unset'};

export const blogPageGlobalStyles = {
  /**
   * Blog article layouts (`page--blog-article-img`, `page--blog-article-content`).
   *
   * Class contract (stable BEM / component hooks used by selectors below):
   *   - `component--*` from `Component` wrapper (`component.jsx`) — e.g. container, breadcrumb,
   *     blog-header-article, product-tiles, editorial-wrapper, section-panel, blog-grid, shop-the-look
   *   - `component--blog-header-article` — explicit on `contentful/models/blog.jsx` root
   *   - `contentful-product-tiles__grid` / `__cta` — `contentful/models/product-tiles.jsx`
   *   - `blog-summary`, `blog-summary--featured`, `blog-summary__*` — `blog-summary.jsx`
   *     (featured image L/R: Blog Grid appearances `Image Left/Right (CF-055)` in `blog-grid.js`)
   *   - `blog-grid__container` — blog-grid style components
   *   - `shop-the-look-block__image-column` — `shop-the-look-block.jsx`
   *   - `editorial-container` — section-panel editorial copy (CF-021)
   *   - `component--rich-text-block` + `.rich-text-content` — rich text block / blog body
   *   - `section-panel-split`, `section-panel-split--5050` — split panel models
   *   - `link link--outlined-light` — Contentful link secondary variant
   *   - `.swiper-range-tile` — in-tile product hero (`ProductTileImageGallery`, progress bar only)
   */
  'body.page:is(.page--blog-article-img, .page--blog-article-content)': {
    '& .component--container': {
      paddingX: unset,
    },
    '& .component--breadcrumb': {
      marginTop: {base: '16px', lg: '48px'},
    },
    '&.page--blog-article-content': {
      '& #app-main > [class*="component"]:not(.component--container):not(.component--blog-header-article):not(.component--breadcrumb):not(.component-container__background), & #app-main > .component--container > [class*="component"]:not(.component-container__background)': {
        paddingTop: {base: '40px', lg: '80px'},
        paddingBottom: unset,
      },
      '& #app-main > .component--container  > .component--container > [class*="component"]:not(.component-container__background)': {
        paddingTop: {base: '40px', lg: '80px'},
        paddingBottom: unset,
        '&:first-child': {
          paddingTop: unset,
        },
      },
      '& .component--product-tiles': {
        '& .contentful-product-tiles__grid + .contentful-product-tiles__cta, & .contentful-product-tiles__grid:not(:has(+ .contentful-product-tiles__cta))': {
          marginBottom: {base: '40px', lg: '80px'},
          fontSize: {base: '14px', lg: '14px'},
          '& p': {
            fontSize: {base: '14px', lg: '14px'},
          },
        },
      },
      // Featured image side (left/right) lives on Blog Grid appearances
      // `Image Left (CF-055)` / `Image Right (CF-055)` — see `contentful/blog-grid`.
      '& .blog-grid__container': {
        paddingTop: {base: '48px', lg: '80px'},
        paddingBottom: {base: '48px', lg: '80px'},
      }
    },
    '&.page--blog-article-img': {
      '& #app-main > [class*="component"]:not(.component--container):not(.component--blog-header-article):not(.component--breadcrumb):not(.component-container__background), & #app-main > .component--container > [class*="component"]:not(.component-container__background)': {
        paddingTop: {base: '40px', lg: '80px'},
        paddingBottom: {base: 'unset', lg: '80px'},
      },
      '& #app-main > .component--container  > .component--container > [class*="component"]:not(.component-container__background)': {
        paddingTop: {base: '40px', lg: '80px'},
        paddingBottom: {base: 'unset', lg: '80px'},
        '&:first-child': {
          paddingTop: unset,
          paddingBottom: unset,
        },
      },
      '& .component--product-tiles': {
        paddingTop: {base: '40px', lg: 'unset'},
        '& .contentful-product-tiles__grid + .contentful-product-tiles__cta, & .contentful-product-tiles__grid:not(:has(+ .contentful-product-tiles__cta))': {
          marginBottom: {base: '40px', lg: 'unset'},
        },
      },
      '& .blog-summary__content': {
        '& .blog-summary__meta-row': {
          marginBottom: {base: '16px', lg: '16px'},
        },
      },
    },
    '& .component--container > .component--shop-the-look:has(.shop-the-look-block__image-column)': {
      paddingX: {base: '20px', lg: 'unset'},
    },
    '& .component--editorial-wrapper': {
      '& .component--section-panel > div': {
        paddingTop: unset,
        paddingBottom: unset,
      },
      '& .embedded-entry-block:has(.component--video)': {
        marginTop: unset,
        marginBottom: unset,
      },
      '& .component--shop-the-look:has(.shop-the-look-block__image-column)': {
        paddingX: unset,
      },
    },
    '&:not(.component--container) .editorial-container': {
      paddingX: {base: '20px', lg: 'unset'},
      paddingY: unset,
    },
    '& .component--rich-text-block .rich-text-content': {
      whiteSpace: 'pre-line',
    },
    '& .amb-markup ul': {
      listStyle: 'none',
      paddingInlineStart: 0,
      marginLeft: 0,
      '& > li': {
        position: 'relative',
        paddingLeft: {base: '22px', lg: '22px'},
        listStyle: 'none',
        '&::before': {
          content: '""',
          position: 'absolute',
          left: 0,
          top: '10px',
          width: '6px',
          height: '6px',
          borderRadius: '100%',
          background: '#8C683B',
        },
      },
    },
    '& .chakra-text:empty': {
      margin: 0,
    },
    '& .component--container:has(.component--product-tiles)': {
      paddingTop: unset,
      paddingBottom: unset,
    },
    '& blockquote': {
      marginTop: {base: '40px', lg: '40px'},
    },
    '& #app-main > [class*="component"]:last-child': {
      paddingBottom: {base: '64px', lg: '80px'},
    },
    '& .section-panel-split': {
      paddingTop: unset,
      paddingBottom: unset,
      '&.section-panel-split--5050': {
        flexFlow: 'row',
      },
    },
    '& [class*="section-panel-split--"]': {
      gap: {base: '20px', lg: 10},
    },
    '& .component--blog-grid .blog-grid__container .link--outlined-light': {
      '& > p': {
        fontSize: {base: '13px', lg: '13px'},
        fontWeight: '600',
        lineHeight: '120%',
      },
      '&:hover': {
        textDecoration: 'none',
      },
    },
  },
  'body.page--blog-editorial-listing': {
    '& .component--blog-listing': {
      paddingTop: unset,
      paddingBottom: {base: '48px', lg: '80px'},
      '& .blog-listing__filter-section-wrapper': {
        paddingY: {base: 'unset', lg: '24px'},
        marginBottom: {base: '32px', lg: 'unset'},
      },
      '& .blog-listing__grid > .blog-grid-listing': {
        gap: {base: '61px', lg: '42px'},
        '& .blog-grid-listing__row--three-col': {
          rowGap: {base: '61px', lg: 'unset'},
        },
      },
    },
    '& .hero-content-wrapper': {
      paddingTop: {base: '48px', lg: '120px'},
      paddingBottom: {base: '32px', lg: '48px'},
    },
  },
  'body.page--blog-landing': {
    '& .section-panel-split': {
      paddingTop: unset,
      paddingBottom: unset,
      '&.section-panel-split--5050': {
        flexFlow: 'row',
      },
    },
    '& .blog-summary--featured .blog-summary__content': {
      backgroundColor: '#F1EDE4',
    },
    '& .hero-content-wrapper': {
      paddingTop: {base: '56px', lg: '120px'},
      paddingBottom: {base: '32px', lg: '48px'},
    },
    '& .component--blog-listing': {
      paddingTop: unset,
      paddingBottom: {base: '32px', lg: '48px'},
    },
    '& .component--container': {
      '& .component--split-heading': {
        paddingTop: {base: '56px', lg: '80px'},
        paddingBottom: {base: 'unset', lg: 'unset'},
      },
      '& .component--media-carousel': {
        paddingBottom: {base: '56px', lg: '100px'},
      },
    },
    '& .component--blog-grid .blog-grid__container': {
      paddingTop: unset,
      gap: {base: '32px', lg: '80px'},
      '& > .chakra-heading': {
        paddingTop: {base: 'unset', lg: '24px'},
        '& + div': {
          marginTop: {base: 'unset', lg: '-56px'},
        },
      },
      '& .blog-grid-listing__cta': {
        marginTop: {base: 'unset', lg: '-40px'},
      }
    },
    '& .hero-content-wrapper': {
      '& .chakra-link': {
        paddingY: {base: '14px', lg: '14px'},
        paddingX: {base: '48px', lg: '48px'},
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      },
    },
    '& .component--container .component--blog-grid': {
      '& .blog-grid__header': {
        marginBottom: unset,
        paddingBottom: unset,
        '& .chakra-text': {
          fontSize: {base: '16px', lg: '18px'},
          fontStyle: 'normal',
          fontWeight: '400',
          lineHeight: '160%',
        },
      },
      '& .blog-summary__content--featured-shop-the-look': {
        '& .chakra-text:first-child': {
          fontSize: '12px',
          fontStyle: 'normal',
          fontWeight: '600',
          lineHeight: '120%',
          letterSpacing: '2.4px',
          '& .chakra-link': {
            '& .chakra-heading': {
              fontSize: {base: '28px', lg: '32px'},
            },
          },
        },
      },
      '& .component--blog': {
        gap: {base: '16px', lg: '24px'},
      },
      '& .blog-grid__container': {
        '& .blog-grid-listing__row--three-col': {
          paddingTop: unset,
          paddingBottom: unset,
        },
        '&:has(.blog-grid__header)': {
          paddingTop: {base: '56px', lg: '80px'},
        },
        '& .blog-grid__header': {
          marginBottom: {base: '8px', lg: 'unset'},
        },
      },
    },
    '& .blog-summary__content--featured-shop-the-look': {
      paddingleft: {base: '32px', lg: '48px'},
      paddingRight: {base: '32px', lg: '48px'},
      '& .blog-summary__category': {
        fontSize: {base: '12px', lg: '12px'},
        marginBottom: {base: '16px', lg: '16px'},
      },
      '& .blog-summary__title-link .blog-summary__title': {
        fontSize: {base: '20px', lg: '32px'},
      },
    },
    '& .component--split-heading': {
      gap: {base: '16px', lg: '40px'},
      '& h1, h2, h3, h4, h5, h6': {
        fontWeight: '500',
        letterSpacing: {base: '-0.28px', lg: '-0.4px'},
      },
      '& p': {
        fontSize: {base: '16px', lg: '18px'},
      },
    },
    '& .component--blog-grid': {
      '& .blog-grid__header': {
        '& .chakra-heading': {
          color: 'brandColors.greyscale90',
          fontFamily: 'heading',
          fontSize: {base: '28px', lg: '40px'},
          fontStyle: 'normal',
          fontWeight: 500,
          lineHeight: '120%',
          letterSpacing: {base: '-0.28px', lg: '-0.4px'},
          fontVariantNumeric: 'lining-nums proportional-nums',
        },
        '& .chakra-text': {
          color: 'brandColors.greyscale90',
          fontFamily: 'body',
          fontSize: {base: '16px', lg: '18px'},
          fontStyle: 'normal',
          fontWeight: 400,
          lineHeight: '160%',
          marginTop: unset,
        },
      },
    },
    '& #app-main > .component--hero-banner': {
      '& .hero-content-wrapper': {
        paddingTop: {base: '56px', lg: '120px'},
        paddingBottom: {base: '32px', lg: '48px'},
      },
    },
    '& .component-container__background': {
      '& .component--container:has( .component--hero-banner)': {
        '& .component--hero-banner': {
          paddingY: {base: '20px', lg: '120px'},
          paddingX: {base: '20px', lg: '80px'},
          '& .component--action-links:hover p, & .component--action-links:hover': {
            textDecoration: 'none',
          }
        },
      },
    },
  },
};
